import React, { useState, useEffect } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { getServiceById, updateService } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { 
  IndividualEditPageLayout, 
  SidebarSection, 
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { 
  ArrowLeft,
  Wrench,
  DollarSign, 
  Tag, 
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Clock,
  Star,
  Award,
  Plus,
  X
} from 'lucide-react';

interface ServiceFormData {
  title: string;
  description: string;
  category: 'TUTORING' | 'CLEANING' | 'REPAIR' | 'PERSONAL_CARE' | 'TRANSPORTATION' | 'EVENT_PLANNING' | 'PHOTOGRAPHY' | 'CONSULTING' | 'OTHER';
  price: string;
  priceType: 'HOURLY' | 'FIXED' | 'PER_SESSION' | 'FREE';
  duration: string;
  availability: 'AVAILABLE' | 'LIMITED' | 'BUSY';
  experience: string;
  qualifications: string[];
  contactInfo: string;
}

const SERVICE_CATEGORIES = [
  { value: 'TUTORING', label: 'Tutoring', description: 'Educational support and teaching' },
  { value: 'CLEANING', label: 'Cleaning', description: 'House cleaning, office cleaning, etc.' },
  { value: 'REPAIR', label: 'Repair', description: 'Electronics, appliances, furniture repair' },
  { value: 'PERSONAL_CARE', label: 'Personal Care', description: 'Hair, beauty, wellness services' },
  { value: 'TRANSPORTATION', label: 'Transportation', description: 'Rides, delivery, moving help' },
  { value: 'EVENT_PLANNING', label: 'Event Planning', description: 'Party planning, coordination' },
  { value: 'PHOTOGRAPHY', label: 'Photography', description: 'Photo shoots, events, portraits' },
  { value: 'CONSULTING', label: 'Consulting', description: 'Business, legal, technical advice' },
  { value: 'OTHER', label: 'Other', description: 'Any other service not listed' }
];

const PRICE_TYPE_OPTIONS = [
  { value: 'HOURLY', label: 'Hourly', description: 'Charged per hour of work' },
  { value: 'FIXED', label: 'Fixed', description: 'One-time fixed price' },
  { value: 'PER_SESSION', label: 'Per Session', description: 'Charged per session/meeting' },
  { value: 'FREE', label: 'Free', description: 'No charge for this service' }
];

const AVAILABILITY_OPTIONS = [
  { value: 'AVAILABLE', label: 'Available', description: 'Currently accepting new clients' },
  { value: 'LIMITED', label: 'Limited', description: 'Limited availability' },
  { value: 'BUSY', label: 'Busy', description: 'Currently not accepting new clients' }
];

// Tooltip component
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default function EditServicePage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { itemId, slug } = useParams<{ itemId: string; slug?: string }>();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const servicesListPath = locationSectionPath(locSlug, 'services');
  
  const updateServiceFn = useAction(updateService);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<ServiceFormData>({
    title: '',
    description: '',
    category: 'OTHER',
    price: '',
    priceType: 'FIXED',
    duration: '',
    availability: 'AVAILABLE',
    experience: '',
    qualifications: [],
    contactInfo: ''
  });

  const [newQualification, setNewQualification] = useState('');

  // Fetch the service data
  const { data: service, isLoading, error } = useQuery(getServiceById, { itemId: itemId! });

  // Populate form when service data is loaded
  useEffect(() => {
    if (service) {
      setFormData({
        title: service.title || '',
        description: service.description || '',
        category: service.metadata?.category || 'OTHER',
        price: service.metadata?.price ? service.metadata.price.toString() : '',
        priceType: service.metadata?.priceType || 'FIXED',
        duration: service.metadata?.duration || '',
        availability: service.metadata?.availability || 'AVAILABLE',
        experience: service.metadata?.experience || '',
        qualifications: service.metadata?.qualifications || [],
        contactInfo: service.metadata?.contactInfo || ''
      });
    }
  }, [service]);

  const handleChange = (field: keyof ServiceFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addQualification = () => {
    if (newQualification.trim() && !formData.qualifications.includes(newQualification.trim())) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, newQualification.trim()]
      }));
      setNewQualification('');
    }
  };

  const removeQualification = (qualification: string) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter(q => q !== qualification)
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.experience.trim()) {
      newErrors.experience = 'Experience description is required';
    }

    if (formData.priceType !== 'FREE' && !formData.price.trim()) {
      newErrors.price = 'Price is required for paid services';
    }

    if (formData.priceType !== 'FREE' && (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0)) {
      newErrors.price = 'Please enter a valid price';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await updateServiceFn({
        itemId: itemId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        price: formData.priceType === 'FREE' ? undefined : parseFloat(formData.price),
        priceType: formData.priceType,
        duration: formData.duration.trim(),
        availability: formData.availability,
        experience: formData.experience.trim(),
        qualifications: formData.qualifications,
        contactInfo: formData.contactInfo.trim()
      });

      // Navigate back to the services page
      handleGoBack();
    } catch (error) {
      console.error('Error updating service:', error);
      setErrors({ submit: 'Failed to update service. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(servicesListPath);
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be logged in to edit services.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading service...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !service) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Service Not Found</h3>
            <p className="text-gray-600">The service you're looking for doesn't exist or you don't have permission to edit it.</p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Check if user owns this service
  if (service.authorId !== user.id) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You can only edit your own services.</p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="service-form"
            disabled={isSubmitting || !formData.title.trim() || !formData.experience.trim()}
            icon={Save}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Updating...
              </>
            ) : (
              'Update Service'
            )}
          </ActionButton>
          
          <ActionButtonSecondary
            onClick={handleGoBack}
            disabled={isSubmitting}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Tips for Better Results">
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Use clear, descriptive titles that explain what you offer</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Include detailed descriptions of your service and process</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Highlight your experience and relevant qualifications</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Set fair prices based on your skill level and market rates</span>
          </div>
          <div className="flex items-start gap-2">
            <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>Be responsive to inquiries to build trust with potential clients</span>
          </div>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout sidebarContent={sidebarContent}>
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Wrench className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Edit Service</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="service-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* Service Title */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What service are you offering?</h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="e.g., Math Tutoring, House Cleaning, Computer Repair"
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Service Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder="Describe your service in detail, what you offer, how you work, etc..."
                      required
                    />
                    {errors.description && (
                      <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">0/1000 characters</p>
                  </div>
                </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleChange('category', e.target.value as any)}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                      >
                        {SERVICE_CATEGORIES.map(category => (
                          <option key={category.value} value={category.value}>
                            {category.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Price Type and Price */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Price Type</label>
                        <select
                          value={formData.priceType}
                          onChange={(e) => handleChange('priceType', e.target.value as any)}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          {PRICE_TYPE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {formData.priceType !== 'FREE' && (
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            <div className="flex items-center gap-2">
                              <span>Price</span>
                              <Tooltip content="Enter your service price">
                                <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                              </Tooltip>
                            </div>
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.price}
                              onChange={(e) => handleChange('price', e.target.value)}
                              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                                errors.price ? 'border-destructive bg-destructive/10' : 'border-input'
                              }`}
                              placeholder="0.00"
                              required={true}
                            />
                          </div>
                          {errors.price && (
                            <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {errors.price}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Duration */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Duration</span>
                          <Tooltip content="How long does this service typically take?">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.duration}
                          onChange={(e) => handleChange('duration', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="e.g., 1 hour, 2-3 hours, 1 day"
                        />
                      </div>
                    </div>

                    {/* Availability */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Availability</label>
                      <select
                        value={formData.availability}
                        onChange={(e) => handleChange('availability', e.target.value as any)}
                        className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                      >
                        {AVAILABILITY_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Experience */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Experience</span>
                          <Tooltip content="Describe your relevant experience and background">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <textarea
                        value={formData.experience}
                        onChange={(e) => handleChange('experience', e.target.value)}
                        rows={3}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                          errors.experience ? 'border-destructive bg-destructive/10' : 'border-input'
                        }`}
                        placeholder="Describe your experience, years of practice, specializations..."
                        required
                      />
                      {errors.experience && (
                        <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.experience}
                        </p>
                      )}
                    </div>

                    {/* Qualifications */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Qualifications</span>
                          <Tooltip content="Add certifications, degrees, or other qualifications">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newQualification}
                            onChange={(e) => setNewQualification(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQualification())}
                            className="flex-1 px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                            placeholder="e.g., Certified Electrician, PhD in Mathematics"
                          />
                          <button
                            type="button"
                            onClick={addQualification}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        {formData.qualifications.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {formData.qualifications.map((qual, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                              >
                                {qual}
                                <button
                                  type="button"
                                  onClick={() => removeQualification(qual)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        <div className="flex items-center gap-2">
                          <span>Contact Information</span>
                          <Tooltip content="How clients can reach you">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.contactInfo}
                          onChange={(e) => handleChange('contactInfo', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="Phone number or email"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
    </IndividualEditPageLayout>
  );
}



